import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Collapse,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Business,
  LocationOn,
  Phone,
  Email,
  Language,
  Schedule,
  CheckCircle,
  Visibility,
} from '@mui/icons-material';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';
import { useBusinessData } from '../../context/BusinessDataContext';
import MediaPreviewDialog from './MediaPreviewDialog';

const PAGE_SIZE = 25;

const Partners = () => {
  const { db } = useRegionContext();
  const { partners: partnersData, verifiedLoading: loading, verifiedError: error, loadVerified, refreshVerified } = useBusinessData();
  const [expandedId, setExpandedId] = useState(null);
  const [previewVenue, setPreviewVenue] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (partnersData === null) loadVerified();
  }, [partnersData, loadVerified]);

  const partners = partnersData || [];

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading partners...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (partners.length === 0) {
    return (
      <Alert severity="info">
        No verified partners found
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Total Partners: {partners.length}
        </Typography>
        <Button onClick={refreshVerified} variant="outlined">
          Refresh
        </Button>
      </Box>
      <TabDescription>
        Fully onboarded partner venues — verified and Stripe-connected, ready to accept live orders. Data
        comes from the Business collection where bizVerified is true and bizStripeId is present. Use this
        list to monitor the active partner base, review venue details, and track which partners joined most
        recently.
      </TabDescription>

      {/* Pagination controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={page === 0}
          onClick={() => { setPage(p => p - 1); setExpandedId(null); }}
        >
          Previous
        </Button>
        <Typography variant="body2">
          Page {page + 1} of {Math.ceil(partners.length / PAGE_SIZE)} ({partners.length} total)
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={(page + 1) * PAGE_SIZE >= partners.length}
          onClick={() => { setPage(p => p + 1); setExpandedId(null); }}
        >
          Next
        </Button>
      </Box>

      <List>
        {partners.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((partner) => (
          <Card key={partner.id} sx={{ mb: 2 }}>
            <ListItem sx={{ py: 2 }}>
              <ListItemText
                sx={{ cursor: 'pointer', flex: 1 }}
                onClick={() => handleToggleExpand(partner.id)}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Business color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {partner.bizName || 'Unnamed Business'}
                    </Typography>
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    {partner.bizStripeId && (
                      <Chip label="Stripe Connected" size="small" color="success" />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {partner.bizAddress || 'No address provided'} •{' '}
                    Verified {(partner.verifiedAt || partner.importedAt)?.toDate?.()?.toLocaleDateString?.() || 'Unknown'}
                  </Typography>
                }
              />
              <IconButton
                size="small"
                onClick={() => setPreviewVenue(partner)}
                title="Preview media"
                sx={{ mr: 0.5 }}
              >
                <Visibility />
              </IconButton>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleToggleExpand(partner.id)}>
                {expandedId === partner.id ? <ExpandLess /> : <ExpandMore />}
              </Box>
            </ListItem>

            <Collapse in={expandedId === partner.id} timeout="auto" unmountOnExit>
              <CardContent sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Business Details
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      <Typography><strong>Type:</strong> {partner.bizType || 'N/A'}</Typography>
                      <Typography><strong>Cuisine:</strong> {partner.bizCuisines?.join(', ') || 'N/A'}</Typography>
                      <Typography><strong>Average Price:</strong> ${partner.bizAveragePrice || 'N/A'}</Typography>
                      {partner.bizStripeId && (
                        <Typography><strong>Stripe ID:</strong> {partner.bizStripeId}</Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Address
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      <Typography>
                        {[
                          partner.bizUnitNo,
                          partner.bizStreetNo,
                          partner.bizStreetName,
                          partner.bizStreetType,
                        ].filter(Boolean).join(' ')}
                      </Typography>
                      <Typography>
                        {partner.bizSuburb} {partner.bizState} {partner.bizPostCode}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Contact */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contact Information
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {partner.bizPhone && (
                        <Typography>
                          <Phone sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {partner.bizPhone}
                        </Typography>
                      )}
                      {partner.bizEmail && (
                        <Typography>
                          <Email sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {partner.bizEmail}
                        </Typography>
                      )}
                      {partner.bizWeb && (
                        <Typography>
                          <Language sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          <a href={partner.bizWeb} target="_blank" rel="noopener noreferrer">
                            {partner.bizWeb}
                          </a>
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Trading Hours */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Trading Hours
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                        const open = partner[`biz${day}Open`];
                        const close = partner[`biz${day}Close`];
                        return (
                          <Typography key={day} variant="body2">
                            <strong>{day}:</strong> {open && close ? `${open} - ${close}` : 'Closed'}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Grid>

                  {/* Seating */}
                  {partner.bizTableTotal && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Seating
                      </Typography>
                      <Box sx={{ pl: 3 }}>
                        <Typography><strong>Total Tables:</strong> {partner.bizTableTotal}</Typography>
                        <Typography><strong>Total Seats:</strong> {partner.bizTotalSeatNo}</Typography>
                        {partner.bizInsideLowTableNo && (
                          <Typography>Inside Low: {partner.bizInsideLowTableNo} tables, {partner.bizInsideLowSeatNo} seats</Typography>
                        )}
                        {partner.bizOutsideLowTableNo && (
                          <Typography>Outside: {partner.bizOutsideLowTableNo} tables, {partner.bizOutsideLowSeatNo} seats</Typography>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Accessibility */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Accessibility Features
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {partner.bizDisBath && <Chip label="Accessible Bathroom" size="small" sx={{ mr: 1, mb: 1 }} />}
                      {partner.disParking && <Chip label="Accessible Parking" size="small" sx={{ mr: 1, mb: 1 }} />}
                      {partner.bizEntry === 'Ramp' && <Chip label="Ramp Entry" size="small" sx={{ mr: 1, mb: 1 }} />}
                    </Box>
                  </Grid>

                  {/* Images */}
                  {partner.bizImages && partner.bizImages.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Images
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {partner.bizImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Business ${idx + 1}`}
                            style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 8 }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        ))}
      </List>

      {previewVenue && (
        <MediaPreviewDialog
          open={!!previewVenue}
          onClose={() => setPreviewVenue(null)}
          business={previewVenue}
        />
      )}
    </Box>
  );
};

export default Partners;
